import { TextField } from '@side/pantry';
import type { ChangeEvent } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import {
  toYearNumber,
  YEAR_MAX_LENGTH,
} from 'src/views/Coversheet/utils/fieldValidation';
import type { RHFBoundProps } from './types';

export type YearFieldProps<TFieldValues extends FieldValues = FieldValues> =
  RHFBoundProps<TFieldValues, number | null | undefined> & {
    label: string;
    required?: boolean;
    disabled?: boolean;
    onBlur?: () => void;
  };

export function YearField<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  rules,
  shouldUnregister,
  label,
  required,
  disabled,
  onBlur: customOnBlur,
}: YearFieldProps<TFieldValues>) {
  const {
    field: { value, onChange, onBlur: rhfOnBlur, ref },
    fieldState: { error, isTouched },
  } = useController({ control, name, rules, shouldUnregister });

  const showError = !!error && isTouched;

  return (
    <TextField
      label={label}
      name={name}
      value={typeof value === 'number' ? String(value) : ''}
      placeholder="YYYY"
      disabled={disabled}
      required={required}
      fullWidth
      fixedHeight
      ref={ref}
      error={showError}
      helperText={showError ? error?.message : undefined}
      highlight={
        required && typeof value !== 'number' ? 'secondary' : undefined
      }
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        onChange(toYearNumber(event.target.value));
      }}
      onBlur={() => {
        rhfOnBlur();
        customOnBlur?.();
      }}
      slotProps={{
        htmlInput: {
          inputMode: 'numeric',
          maxLength: YEAR_MAX_LENGTH,
        },
      }}
    />
  );
}
